/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import DynamicView from "./DynamicView";
import StaticView from "./StaticView";
import CustomTitle from "../FormTitle";
import LocalLoader from "../../../Loaders/Local";

import { useGetApiDataQuery } from "../../../../api/forms.api.slice";
import { ReviewContext } from "../../context/reviewItemContext";
import {
  appendDg,
  appendDgKeys,
  appendDocumentList,
} from "../../context/formSlice";
import { showNotification } from "../../../../features/core/slices/notification.slice";
import {
  getFormattedDataForDg,
  constructSearchUrl,
  formConditional,
  getParsedValue,
  generateUrlParams,
  getWatchFieldName,
} from "../../services/form.service";
import { notifyError } from "../../../../utils/helpers";

function Datagrid(props) {
  const { gridItem } = props;
  const { key, properties } = gridItem;
  const params = useParams();
  const dispatch = useDispatch();
  const { reviewProps } = useContext(ReviewContext);
  const formConfig = useSelector((state) => state.formProps.formConfig);
  const formData = useSelector((state) => state.formProps.formData);
  const applicationStatus = useSelector(
    (state) => state.formProps.applicationData.status
  );
  const { getValues, setValue } = useFormContext();
  const [req, setReq] = useState("");
  const [skip, setSkip] = useState(true);
  const {
    data: apiData,
    isFetching,
    isSuccess,
    error,
  } = useGetApiDataQuery(req, { skip });
  const dgConfigRef = useRef();

  if (!dgConfigRef.current && gridItem.validate?.custom) {
    dgConfigRef.current = getParsedValue(
      gridItem.validate.custom,
      gridItem.key
    );
  }
  const { sourceDatagrid, identifierLabel } = dgConfigRef.current || {};
  const { associatedDatagrids } = properties || {};
  const isCategorizedView = Boolean(
    sourceDatagrid && identifierLabel && !associatedDatagrids
  );

  let watchFieldValue = useWatch({
    name: getWatchFieldName(gridItem),
    disabled: !gridItem.conditional || !gridItem.conditional.when,
  });
  // hook form wont reference field not present in form
  if (
    watchFieldValue === undefined &&
    formData[gridItem.conditional.when] !== undefined
  ) {
    watchFieldValue = formData[gridItem.conditional.when];
  }
  const shouldDisplay = formConditional(gridItem, watchFieldValue, getValues());

  useEffect(() => {
    const { url, type, fetchOnStatus, cloneIndicator } = properties || {};

    let canFetchRecord = false;
    if (!fetchOnStatus) {
      canFetchRecord = true;
    }
    if (fetchOnStatus) {
      canFetchRecord = fetchOnStatus.split(",").includes(applicationStatus);
    }

    canFetchRecord =
      canFetchRecord &&
      (!cloneIndicator || (cloneIndicator && !formData[cloneIndicator]));

    if (canFetchRecord && url && type) {
      const searchUri = constructSearchUrl(url, { ...formData, ...params });
      const urlParamsObject = generateUrlParams(searchUri);
      setReq(urlParamsObject);
      setSkip(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { url, type, prefix, cloneIndicator } = properties || {};
    if (url && type && isSuccess) {
      const dgData = getFormattedDataForDg(apiData, type, prefix);
      if (["Approved", "Deactivated", "Expired"].includes(applicationStatus))
        dispatch(appendDocumentList({ documents: dgData.documents }));
      setValue(key, dgData.formData);
      dispatch(appendDgKeys({ [key]: dgData.formData }));
      if (formConfig.readOnly)
        dispatch(appendDg({ gridKey: key, value: dgData.formData }));
      if (cloneIndicator) setValue(cloneIndicator, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridItem.key, apiData, isSuccess]);

  useEffect(() => {
    if (error) {
      if (properties && properties.url && properties.type) setValue(key, []);
      notifyError(dispatch, showNotification, error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, error]);

  if (shouldDisplay) {
    return (
      <>
        {!gridItem.hideLabel && gridItem.label !== "" && (
          <CustomTitle
            title={gridItem.label}
            showProTip={gridItem?.tags && gridItem?.tags.includes("proTip")}
            fieldKey={gridItem.key}
            header
          />
        )}
        <Box sx={{ position: "relative", minHeight: "4rem" }}>
          <LocalLoader progress={isFetching} />
          {!isFetching &&
            (!reviewProps ? (
              <DynamicView
                gridItem={gridItem}
                formData={formData}
                dgConfigRef={dgConfigRef}
                isCategorizedView={isCategorizedView}
              />
            ) : (
              <StaticView
                gridItem={gridItem}
                dgConfigRef={dgConfigRef}
                isCategorizedView={isCategorizedView}
              />
            ))}
        </Box>
      </>
    );
  }
  return "";
}
Datagrid.propTypes = {
  gridItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    hideLabel: PropTypes.bool,
    label: PropTypes.string,
    components: PropTypes.oneOfType([PropTypes.array]).isRequired,
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
  }).isRequired,
};
// Datagrid.whyDidYouRender = true;
export default Datagrid;
